import {
  normalizeBuiltInCommonMeasureId,
} from "../shared/commonMeasures";

export const CATALOG_FOOD_SOURCE_EFORT = "EFORT";
export const CATALOG_FOOD_SOURCE_OPEN_SOURCE = "OPEN_SOURCE";
export const CATALOG_FOOD_KIND_GENERIC = "generic";
export const CATALOG_FOOD_KIND_BRANDED = "branded";
export const DEFAULT_SUPABASE_FOOD_SCHEMA = "api";
export const DEFAULT_SUPABASE_FOOD_VIEW = "catalog_foods_search";
export const DEFAULT_SUPABASE_FOOD_COLUMNS = [
  "id",
  "name",
  "type",
  "source",
  "code",
  "external_source_id",
  "name_es",
  "name_en",
  "notes",
  "reference_quantity_g",
  "selected_measure_id",
  "common_measures",
  "nutrients_per_100g",
  "allergen_codes",
  "allergen_data_complete",
  "is_vegetarian",
  "is_vegan",
  "dietary_data_complete",
  "dietary_suitability",
  "brand",
  "store",
  "website",
  "origin",
  "schema_version",
  "created_at",
  "updated_at",
  "calories",
  "protein",
  "carbs",
  "fats",
  "fiber",
  "catalog_kind",
  "is_branded",
].join(",");
export const DEFAULT_SUPABASE_FOOD_LIMIT = 40;
export const DEFAULT_SUPABASE_FOOD_ORDER_COLUMN = "name";
export const DEFAULT_SUPABASE_FOOD_SEARCH_COLUMNS = Object.freeze([
  "name",
  "name_en",
  "name_es",
  "brand",
]);
export const DEFAULT_SUPABASE_FOOD_NORMALIZED_SEARCH_COLUMNS = Object.freeze([
  "search_name_norm",
  "search_name_en_norm",
  "search_name_es_norm",
  "search_brand_norm",
  "search_text_norm",
]);
export const DEFAULT_SUPABASE_FOOD_EXACT_SEARCH_COLUMNS = Object.freeze([
  "search_name_norm",
  "search_name_en_norm",
  "search_name_es_norm",
  "search_brand_norm",
]);
export const DEFAULT_SUPABASE_FOOD_PREFIX_SEARCH_COLUMNS = Object.freeze([
  "search_name_norm",
  "search_name_en_norm",
  "search_name_es_norm",
  "search_brand_norm",
]);
export const DEFAULT_SUPABASE_FOOD_VIEW_COLUMNS = [
  DEFAULT_SUPABASE_FOOD_COLUMNS,
  "search_code_norm",
  "search_name_norm",
  "search_name_en_norm",
  "search_name_es_norm",
  "search_brand_norm",
  "search_text_norm",
].join(",");
export const SUPABASE_FOOD_SEARCH_TIER_LIMITS = Object.freeze({
  exactCode: 8,
  exactText: 16,
  prefix: 32,
  fullText: 48,
});
export const MIN_FUZZY_FOOD_SEARCH_QUERY_LENGTH = 4;
const LEGACY_EFORT_SOURCES = new Set([
  "SUPABASE_GENERIC_FOOD",
  "GENERIC_SUPPLEMENT",
]);
const LEGACY_OPEN_SOURCE_SOURCES = new Set(["BEDCA"]);

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function getFiniteNumber(value, fallback = 0) {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

function getFirstTextValue(record, keys, fallback = "") {
  const resolvedKeys = Array.isArray(keys) ? keys : [keys];

  for (const key of resolvedKeys) {
    if (hasText(record?.[key])) {
      return record[key].trim();
    }
  }

  return fallback;
}

function getFirstFiniteNumber(record, keys, fallback = 0) {
  const resolvedKeys = Array.isArray(keys) ? keys : [keys];

  for (const key of resolvedKeys) {
    const value = getFiniteNumber(record?.[key], Number.NaN);
    if (Number.isFinite(value)) {
      return value;
    }
  }

  return fallback;
}

function normalizeCatalogFoodCommonMeasures(commonMeasures) {
  if (!Array.isArray(commonMeasures)) {
    return [];
  }

  const seenIds = new Set();

  return commonMeasures
    .map((measure) => {
      const measureId = normalizeBuiltInCommonMeasureId(
        measure?.measureKey ?? measure?.id,
      );
      const grams = getFiniteNumber(measure?.grams, 0);

      if (!measureId || grams <= 0 || seenIds.has(measureId)) {
        return null;
      }

      seenIds.add(measureId);

      return {
        id: measureId,
        grams,
      };
    })
    .filter(Boolean);
}

function inflateCatalogFoodCommonMeasures(commonMeasures) {
  return normalizeCatalogFoodCommonMeasures(commonMeasures).map((measure) => ({
    id: measure.id,
    measureKey: measure.id,
    grams: measure.grams,
  }));
}

function normalizeCatalogFoodSelectedMeasureId(selectedMeasureId, commonMeasures) {
  if (String(selectedMeasureId ?? "").trim() === "grams") {
    return null;
  }

  const normalizedSelectedMeasureId = normalizeBuiltInCommonMeasureId(
    selectedMeasureId,
  );
  if (!normalizedSelectedMeasureId) {
    return null;
  }

  return normalizeCatalogFoodCommonMeasures(commonMeasures).some(
    (measure) => measure.id === normalizedSelectedMeasureId,
  )
    ? normalizedSelectedMeasureId
    : null;
}

export function normalizeCatalogFoodSource(
  value,
  { defaultSource = CATALOG_FOOD_SOURCE_EFORT } = {},
) {
  const normalizedValue = String(value ?? "").trim().toUpperCase();

  if (
    normalizedValue === CATALOG_FOOD_SOURCE_EFORT ||
    LEGACY_EFORT_SOURCES.has(normalizedValue)
  ) {
    return CATALOG_FOOD_SOURCE_EFORT;
  }

  if (
    normalizedValue === CATALOG_FOOD_SOURCE_OPEN_SOURCE ||
    LEGACY_OPEN_SOURCE_SOURCES.has(normalizedValue)
  ) {
    return CATALOG_FOOD_SOURCE_OPEN_SOURCE;
  }

  return defaultSource;
}

function normalizeBoolean(value, fallback = null) {
  if (typeof value === "boolean") {
    return value;
  }

  const normalizedValue = String(value ?? "").trim().toLowerCase();
  if (["true", "1", "yes"].includes(normalizedValue)) {
    return true;
  }
  if (["false", "0", "no"].includes(normalizedValue)) {
    return false;
  }

  return fallback;
}

export function normalizeCatalogFoodKind(value, record = {}) {
  const normalizedValue = String(value ?? "").trim().toLowerCase();
  if (normalizedValue === CATALOG_FOOD_KIND_BRANDED) {
    return CATALOG_FOOD_KIND_BRANDED;
  }
  if (normalizedValue === CATALOG_FOOD_KIND_GENERIC) {
    return CATALOG_FOOD_KIND_GENERIC;
  }

  const isBranded = normalizeBoolean(record?.is_branded ?? record?.isBranded);
  if (isBranded === true) {
    return CATALOG_FOOD_KIND_BRANDED;
  }
  if (isBranded === false) {
    return CATALOG_FOOD_KIND_GENERIC;
  }

  const identity = normalizeFoodSearchCode(
    `${record?.code ?? ""} ${record?.id ?? ""}`,
  );
  if (
    identity.includes("branded-v1-") ||
    identity.includes("efort-branded-v1-")
  ) {
    return CATALOG_FOOD_KIND_BRANDED;
  }

  return CATALOG_FOOD_KIND_GENERIC;
}

function buildDefaultNutrientsPer100g(food) {
  return [
    { id: "calories", value: food.calories },
    { id: "protein", value: food.protein },
    { id: "carbs", value: food.carbs },
    { id: "fats", value: food.fats },
    { id: "fiber", value: food.fiber },
  ].filter((entry) => entry.value !== 0);
}

export function normalizeFoodSearchText(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

export function normalizeFoodSearchCode(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "");
}

export function isLikelyFoodCodeSearchQuery(value) {
  const trimmedValue = String(value ?? "").trim();
  if (!trimmedValue) {
    return false;
  }

  return /[\d_-]/.test(trimmedValue);
}

export function shouldRunFuzzyFoodSearch(value) {
  return (
    normalizeFoodSearchText(value).length >= MIN_FUZZY_FOOD_SEARCH_QUERY_LENGTH &&
    !isLikelyFoodCodeSearchQuery(value)
  );
}

export function tokenizeFoodSearchText(value) {
  return normalizeFoodSearchText(value)
    .split(" ")
    .map((token) => token.trim())
    .filter(Boolean);
}

export function escapeSupabaseLikeValue(value) {
  return String(value ?? "")
    .replaceAll("\\", "\\\\")
    .replaceAll("%", "\\%")
    .replaceAll("_", "\\_");
}

export function normalizeSupabaseFoodRecord(record) {
  if (!record || typeof record !== "object") {
    return null;
  }

  const id = getFirstTextValue(record, ["id", "code"]);
  const localizedName = getFirstTextValue(record, [
    "name",
    "display_name",
    "name_en",
    "name_es",
  ]);

  if (!id && !localizedName) {
    return null;
  }

  const normalizedCommonMeasures = normalizeCatalogFoodCommonMeasures(
    record?.common_measures ?? record?.commonMeasures,
  );
  const inflatedCommonMeasures = inflateCatalogFoodCommonMeasures(
    normalizedCommonMeasures,
  );
  const selectedMeasureId = normalizeCatalogFoodSelectedMeasureId(
    record?.selected_measure_id ?? record?.selectedMeasureId,
    normalizedCommonMeasures,
  );
  const catalogKind = normalizeCatalogFoodKind(
    record?.catalog_kind ?? record?.catalogKind,
    record,
  );

  const normalizedRecord = {
    ...record,
    id: id || localizedName,
    code: getFirstTextValue(record, ["code"], id || localizedName),
    source: normalizeCatalogFoodSource(getFirstTextValue(record, ["source"]), {
      defaultSource: CATALOG_FOOD_SOURCE_EFORT,
    }),
    catalog_kind: catalogKind,
    catalogKind,
    is_branded: catalogKind === CATALOG_FOOD_KIND_BRANDED,
    isBranded: catalogKind === CATALOG_FOOD_KIND_BRANDED,
    _catalog: true,
    _readOnly: true,
    name: getFirstTextValue(record, ["name", "display_name"], localizedName),
    name_en: getFirstTextValue(record, ["name_en", "name"], localizedName),
    name_es: getFirstTextValue(record, ["name_es", "name"], localizedName),
    brand: getFirstTextValue(record, ["brand"]),
    store: getFirstTextValue(record, ["store"]),
    per: getFirstTextValue(record, ["per"], "100 g"),
    type: getFirstTextValue(record, ["type"], "food"),
    reference_quantity_g: getFirstFiniteNumber(
      record,
      [
        "reference_quantity_g",
        "quantityReferenceG",
        "referenceQuantityG",
        "serving_size_g",
        "serving_g",
        "quantity",
      ],
      100,
    ),
    calories: getFirstFiniteNumber(
      record,
      ["calories", "kcal", "kcal_per_100g", "energy_kcal"],
      0,
    ),
    protein: getFirstFiniteNumber(record, ["protein", "protein_per_100g"], 0),
    carbs: getFirstFiniteNumber(
      record,
      ["carbs", "carbs_per_100g", "carbohydrates", "carbohydrates_per_100g"],
      0,
    ),
    fats: getFirstFiniteNumber(record, ["fats", "fat", "fat_per_100g"], 0),
    fiber: getFirstFiniteNumber(record, ["fiber", "fiber_per_100g"], 0),
    selected_measure_id: selectedMeasureId,
    quantityUnitKey: selectedMeasureId ?? "grams",
    commonMeasures: inflatedCommonMeasures,
    common_measures: inflatedCommonMeasures,
  };

  normalizedRecord.quantityReferenceG = normalizedRecord.reference_quantity_g;
  normalizedRecord.search_code_norm = getFirstTextValue(record, ["search_code_norm"]);
  normalizedRecord.search_name_norm = getFirstTextValue(record, ["search_name_norm"]);
  normalizedRecord.search_name_en_norm = getFirstTextValue(record, ["search_name_en_norm"]);
  normalizedRecord.search_name_es_norm = getFirstTextValue(record, ["search_name_es_norm"]);
  normalizedRecord.search_brand_norm = getFirstTextValue(record, ["search_brand_norm"]);
  normalizedRecord.search_text_norm = getFirstTextValue(record, ["search_text_norm"]);
  normalizedRecord.nutrients_per_100g = Array.isArray(record?.nutrients_per_100g)
    ? record.nutrients_per_100g
    : buildDefaultNutrientsPer100g(normalizedRecord);

  return normalizedRecord;
}

export function normalizeSupabaseFoodRecords(records) {
  return (Array.isArray(records) ? records : [])
    .map((record) => normalizeSupabaseFoodRecord(record))
    .filter(Boolean);
}

export function buildSupabaseFoodSearchClause(
  searchText,
  searchColumns = DEFAULT_SUPABASE_FOOD_SEARCH_COLUMNS,
) {
  const normalizedSearchText = String(searchText || "").trim().replaceAll(",", " ");
  const resolvedColumns = (Array.isArray(searchColumns) ? searchColumns : [])
    .map((column) => String(column || "").trim())
    .filter(Boolean);

  if (!normalizedSearchText || resolvedColumns.length === 0) {
    return "";
  }

  const escapedSearchText = escapeSupabaseLikeValue(normalizedSearchText);
  const pattern = `%${escapedSearchText}%`;

  return resolvedColumns
    .map((column) => `${column}.ilike.${pattern}`)
    .join(",");
}

export function buildSupabaseFoodExactSearchClause(
  searchText,
  searchColumns = DEFAULT_SUPABASE_FOOD_EXACT_SEARCH_COLUMNS,
) {
  const normalizedSearchText = normalizeFoodSearchText(searchText);
  const resolvedColumns = (Array.isArray(searchColumns) ? searchColumns : [])
    .map((column) => String(column || "").trim())
    .filter(Boolean);

  if (!normalizedSearchText || resolvedColumns.length === 0) {
    return "";
  }

  return resolvedColumns
    .map((column) => `${column}.eq.${normalizedSearchText}`)
    .join(",");
}

export function buildSupabaseFoodPrefixSearchClause(
  searchText,
  searchColumns = DEFAULT_SUPABASE_FOOD_PREFIX_SEARCH_COLUMNS,
) {
  const normalizedSearchText = normalizeFoodSearchText(searchText);
  const resolvedColumns = (Array.isArray(searchColumns) ? searchColumns : [])
    .map((column) => String(column || "").trim())
    .filter(Boolean);

  if (!normalizedSearchText || resolvedColumns.length === 0) {
    return "";
  }

  const escapedSearchText = escapeSupabaseLikeValue(normalizedSearchText);

  return resolvedColumns
    .flatMap((column) => [
      `${column}.ilike.${escapedSearchText}%`,
      `${column}.ilike.% ${escapedSearchText}%`,
    ])
    .join(",");
}
